import { supabase } from '@/lib/supabase';
import { isLightViewActionLike } from '@/lib/light-view-action';
import type {
  ActivityFeedArchiveSubjectRequest,
  ActivityFeedArchiveSubjectResponse,
  ActivityFeedCancelTopicWaitRequest,
  ActivityFeedCancelTopicWaitResponse,
  ActivityFeedConfirmOutcomeRequest,
  ActivityFeedConfirmOutcomeResponse,
  ActivityFeedDismissRecommendationRequest,
  ActivityFeedDismissRecommendationResponse,
  ActivityFeedListResponse,
  ActivityFeedMonitorGmailReplyRequest,
  ActivityFeedMonitorGmailReplyResponse,
  ActivityFeedCancelGmailWaitRequest,
  ActivityFeedMonitorExactProviderRequest,
  ActivityFeedMonitorExactProviderResponse,
  ActivityFeedCancelExactProviderWaitRequest,
  ActivityFeedSnoozeTopicRequest,
  ActivityFeedSnoozeTopicResponse,
  ActivityFeedSubjectChatHistoryRequest,
  ActivityFeedSubjectChatHistoryResponse,
  ActivityFeedSubjectDetailRequest,
  ActivityFeedSubjectDetailResponse,
  ActivitySubjectCard,
  ActivitySubjectChatMessage,
  ActivitySubjectDetail,
} from '@shared/types/activity-feed';
import { toDashboardUiError } from '@/lib/dashboard-errors';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isContinuationPlanningStatusLike = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  ['not_requested', 'pending', 'running', 'ready', 'unavailable', 'failed', 'delegated'].includes(
    String(value)
  );

const isEvidenceRunLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.runId === 'string' &&
  (value.toolKey === null || typeof value.toolKey === 'string') &&
  typeof value.label === 'string' &&
  typeof value.eventAt === 'string' &&
  (value.strength === 'weak' || value.strength === 'exact');

const isCountsLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.open === 'number' &&
  typeof value.exactDone === 'number' &&
  typeof value.exactTriggered === 'number' &&
  typeof value.weakCovered === 'number' &&
  typeof value.dismissed === 'number';

const isStatusChipLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.label === 'string' &&
  ['strong', 'weak', 'critical', 'neutral'].includes(String(value.tone));

const isRecommendedActionLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.label === 'string' &&
  (value.summary === null || typeof value.summary === 'string') &&
  [
    'follow_up',
    'meeting_prep',
    'diagnostic',
    'research',
    'crm_update',
    'personalized_email',
    'email',
    'run_review',
    'other',
  ].includes(String(value.kind)) &&
  ['high', 'medium', 'low'].includes(String(value.priority)) &&
  ['open', 'exact_done', 'exact_triggered', 'weak_covered', 'dismissed'].includes(
    String(value.state)
  ) &&
  (value.action === null ||
    value.action === undefined ||
    isLightViewActionLike(value.action)) &&
  (value.dismissible === undefined || typeof value.dismissible === 'boolean') &&
  (value.sourceRunId === undefined ||
    value.sourceRunId === null ||
    typeof value.sourceRunId === 'string') &&
  (value.sourceActionId === undefined ||
    value.sourceActionId === null ||
    typeof value.sourceActionId === 'string') &&
  (value.sourceActionIndex === undefined ||
    value.sourceActionIndex === null ||
    typeof value.sourceActionIndex === 'number') &&
  isContinuationPlanningStatusLike(value.continuationPlanningStatus);

const isThreadDecisionLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  ['requested', 'decided', 'failed', 'superseded'].includes(String(value.status)) &&
  (value.result === null ||
    ['propose_action', 'wait', 'block', 'complete', 'split'].includes(
      String(value.result)
    )) &&
  (value.reason === null || typeof value.reason === 'string') &&
  Array.isArray(value.evidenceRefs) &&
  value.evidenceRefs.every((entry) => typeof entry === 'string') &&
  (value.providerKind === undefined ||
    value.providerKind === null ||
    ['gmail', 'google_calendar', 'hubspot'].includes(String(value.providerKind))) &&
  ['ready', 'refreshing', 'stale', 'unavailable'].includes(String(value.health)) &&
  (value.healthReasonCode === null || typeof value.healthReasonCode === 'string') &&
  (value.confidence === null || typeof value.confidence === 'number') &&
  (value.decidedAt === null || typeof value.decidedAt === 'string');

const isThreadProposalLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.decisionId === 'string' &&
  ['tool', 'capability', 'recipe', 'forge'].includes(String(value.executorKind)) &&
  typeof value.executorKey === 'string' &&
  typeof value.ctaLabel === 'string' &&
  ['active', 'accepted', 'dismissed', 'superseded', 'expired'].includes(
    String(value.status)
  ) &&
  (value.expiresAt === null || typeof value.expiresAt === 'string');

const isThreadActionRunLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  [
    'queued',
    'running',
    'waiting_user',
    'succeeded',
    'partially_succeeded',
    'failed',
    'cancel_requested',
    'cancelled',
  ].includes(String(value.status)) &&
  ['flow', 'task_action', 'contextual_action', 'forge', 'operation'].includes(
    String(value.runtimeKind)
  ) &&
  ['tool', 'capability', 'recipe', 'forge'].includes(String(value.executorKind)) &&
  typeof value.executorKey === 'string' &&
  [
    value.flowExecutionId,
    value.taskActionRunId,
    value.contextualActionPlanId,
    value.forgeConversationId,
  ].every((entry) => entry === null || typeof entry === 'string') &&
  typeof value.acceptedAt === 'string' &&
  (value.startedAt === null || typeof value.startedAt === 'string') &&
  (value.completedAt === null || typeof value.completedAt === 'string') &&
  typeof value.updatedAt === 'string';

const isThreadWaitLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  ['activity_event', 'time', 'manual'].includes(String(value.kind)) &&
  ['exact', 'reminder_only', 'unsupported'].includes(String(value.coverage)) &&
  ['active', 'satisfied', 'expired', 'cancelled'].includes(String(value.status)) &&
  (value.reason === null || typeof value.reason === 'string') &&
  (value.expectedEventType === null || typeof value.expectedEventType === 'string') &&
  (value.correlationSubjectId === null ||
    typeof value.correlationSubjectId === 'string') &&
  (value.providerRefId === null || typeof value.providerRefId === 'string') &&
  (value.wakeAt === null || typeof value.wakeAt === 'string') &&
  (value.expiresAt === null || typeof value.expiresAt === 'string') &&
  typeof value.timezone === 'string' &&
  typeof value.createdAt === 'string' &&
  (value.resolvedAt === null || typeof value.resolvedAt === 'string');

const isThreadGmailWaitCandidateLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.gmailThreadRefId === 'string' &&
  value.coverage === 'exact' &&
  typeof value.watchExpiresAt === 'string';

const isThreadCalendarWaitCandidateLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.googleCalendarEventRefId === 'string' &&
  value.coverage === 'exact' &&
  typeof value.startsAt === 'string' &&
  typeof value.endsAt === 'string';

const isThreadHubSpotWaitCandidateLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.hubSpotObjectRefId === 'string' &&
  value.coverage === 'exact' &&
  value.objectType === 'deal';

const isThreadTopicLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.threadId === 'string' &&
  (value.accountSubjectId === null || typeof value.accountSubjectId === 'string') &&
  (value.primarySubjectId === null || typeof value.primarySubjectId === 'string') &&
  typeof value.semanticKey === 'string' &&
  typeof value.title === 'string' &&
  typeof value.desiredOutcome === 'string' &&
  ['open', 'waiting', 'blocked', 'completed', 'cancelled'].includes(
    String(value.lifecycleState)
  ) &&
  [
    'actionable',
    'needs_preparation',
    'running',
    'awaiting_review',
    'waiting',
    'monitoring',
    'blocked',
    'closed',
  ].includes(String(value.visibleState)) &&
  (value.stateReason === null || typeof value.stateReason === 'string') &&
  [value.expectedValue, value.urgency, value.confidence].every(
    (entry) => entry === undefined || entry === null || typeof entry === 'number'
  ) &&
  (value.priorityRank === null || typeof value.priorityRank === 'number') &&
  (value.prioritySource === null ||
    ['model', 'user', 'policy'].includes(String(value.prioritySource))) &&
  (value.dueAt === undefined ||
    value.dueAt === null ||
    typeof value.dueAt === 'string') &&
  (value.wakeAt === undefined ||
    value.wakeAt === null ||
    typeof value.wakeAt === 'string') &&
  typeof value.version === 'number' &&
  (value.sourceRunId === null || typeof value.sourceRunId === 'string') &&
  (value.sourceActionId === null || typeof value.sourceActionId === 'string') &&
  (value.decision === null || isThreadDecisionLike(value.decision)) &&
  (value.proposal === null || isThreadProposalLike(value.proposal)) &&
  (value.activeActionRun === undefined ||
    value.activeActionRun === null ||
    isThreadActionRunLike(value.activeActionRun)) &&
  (value.activeWait === undefined ||
    value.activeWait === null ||
    isThreadWaitLike(value.activeWait)) &&
  (value.gmailWaitCandidate === undefined ||
    value.gmailWaitCandidate === null ||
    isThreadGmailWaitCandidateLike(value.gmailWaitCandidate)) &&
  (value.calendarWaitCandidate === undefined ||
    value.calendarWaitCandidate === null ||
    isThreadCalendarWaitCandidateLike(value.calendarWaitCandidate)) &&
  (value.hubSpotWaitCandidate === undefined ||
    value.hubSpotWaitCandidate === null ||
    isThreadHubSpotWaitCandidateLike(value.hubSpotWaitCandidate)) &&
  typeof value.openedAt === 'string' &&
  typeof value.updatedAt === 'string' &&
  (value.resolvedAt === null || typeof value.resolvedAt === 'string') &&
  (value.resolutionReason === null || typeof value.resolutionReason === 'string');

const isThreadStateLike = (value: unknown, rich: boolean): boolean =>
  isRecord(value) &&
  (value.source === 'persistent' || value.source === 'legacy') &&
  (value.threadId === null || typeof value.threadId === 'string') &&
  typeof value.subjectId === 'string' &&
  (value.accountSubjectId === null || typeof value.accountSubjectId === 'string') &&
  (value.scopeKind === null ||
    ['account', 'orphan_subject', 'workspace'].includes(String(value.scopeKind))) &&
  ['active', 'quiet', 'archived'].includes(String(value.status)) &&
  typeof value.version === 'number' &&
  ['ready', 'refreshing', 'stale', 'unavailable'].includes(
    String(value.decisionHealth)
  ) &&
  (value.decisionHealthReasonCode === null ||
    typeof value.decisionHealthReasonCode === 'string') &&
  ['idle', 'running', 'failed', 'cancelled'].includes(String(value.executionHealth)) &&
  (value.currentTopic === null || isThreadTopicLike(value.currentTopic)) &&
  Array.isArray(value.otherTopics) &&
  value.otherTopics.every((topic) => isThreadTopicLike(topic)) &&
  typeof value.openTopicCount === 'number' &&
  typeof value.projectedAt === 'string' &&
  (!rich ||
    (Array.isArray(value.topics) &&
      value.topics.every((topic) => isThreadTopicLike(topic))));

const isSubjectCardLike = (value: unknown): value is ActivitySubjectCard =>
  isRecord(value) &&
  (value.subjectId === undefined || typeof value.subjectId === 'string') &&
  (value.accountSubjectId === null || typeof value.accountSubjectId === 'string') &&
  (value.accountAttachmentStatus === null ||
    ['account', 'attached', 'unattached', 'ambiguous'].includes(
      String(value.accountAttachmentStatus)
    )) &&
  (value.subjectKey === null || typeof value.subjectKey === 'string') &&
  typeof value.subjectKind === 'string' &&
  typeof value.subjectLabel === 'string' &&
  (value.subjectSubtitle === null || typeof value.subjectSubtitle === 'string') &&
  (value.imageUrl === undefined || value.imageUrl === null || typeof value.imageUrl === 'string') &&
  typeof value.category === 'string' &&
  typeof value.severity === 'string' &&
  (value.latestEventAt === null || typeof value.latestEventAt === 'string') &&
  (value.lastToolKey === null ||
    value.lastToolKey === undefined ||
    typeof value.lastToolKey === 'string') &&
  (value.lastToolLabel === null ||
    value.lastToolLabel === undefined ||
    typeof value.lastToolLabel === 'string') &&
  typeof value.headline === 'string' &&
  (value.nextStepLabel === null || typeof value.nextStepLabel === 'string') &&
  (value.nextStepAction === null ||
    value.nextStepAction === undefined ||
    isLightViewActionLike(value.nextStepAction)) &&
  (value.recommendedActions === undefined ||
    (Array.isArray(value.recommendedActions) &&
      value.recommendedActions.every((action) => isRecommendedActionLike(action)))) &&
  Array.isArray(value.chips) &&
  value.chips.every((chip) => isStatusChipLike(chip)) &&
  isCountsLike(value.counts) &&
  (value.threadState === undefined ||
    value.threadState === null ||
    isThreadStateLike(value.threadState, false));

const isRecommendationLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.label === 'string' &&
  (value.summary === null || typeof value.summary === 'string') &&
  ['high', 'medium', 'low'].includes(String(value.priority)) &&
  typeof value.state === 'string' &&
  typeof value.stateLabel === 'string' &&
  typeof value.stateTone === 'string' &&
  (value.action === null ||
    value.action === undefined ||
    isLightViewActionLike(value.action)) &&
  Array.isArray(value.evidenceRuns) &&
  value.evidenceRuns.every((run) => isEvidenceRunLike(run)) &&
  typeof value.dismissible === 'boolean' &&
  (value.sourceRunId === null || typeof value.sourceRunId === 'string') &&
  (value.sourceActionId === null || typeof value.sourceActionId === 'string') &&
  (value.sourceActionIndex === null || typeof value.sourceActionIndex === 'number') &&
  isContinuationPlanningStatusLike(value.continuationPlanningStatus);

const isEventLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.eventType === 'string' &&
  typeof value.label === 'string' &&
  (value.detail === null || typeof value.detail === 'string') &&
  typeof value.eventAt === 'string' &&
  typeof value.tone === 'string' &&
  (value.sourceToolKey === null || typeof value.sourceToolKey === 'string') &&
  (value.sourceConnector === null || typeof value.sourceConnector === 'string') &&
  (value.sourceRunId === null || typeof value.sourceRunId === 'string');

const isProofLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.label === 'string' &&
  (value.meta === null || typeof value.meta === 'string') &&
  (value.href === null || typeof value.href === 'string') &&
  typeof value.kind === 'string';

const isThreadItemLike = (value: unknown): boolean =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  (value.kind === 'run' || value.kind === 'event') &&
  typeof value.title === 'string' &&
  (value.summary === null || typeof value.summary === 'string') &&
  (value.eventAt === null || typeof value.eventAt === 'string') &&
  typeof value.tone === 'string' &&
  (value.toolKey === null || typeof value.toolKey === 'string') &&
  (value.toolLabel === null || typeof value.toolLabel === 'string') &&
  (value.sourceRunId === null || typeof value.sourceRunId === 'string') &&
  (value.primaryAction === null ||
    value.primaryAction === undefined ||
    isLightViewActionLike(value.primaryAction)) &&
  (value.taskActionRunId === undefined ||
    value.taskActionRunId === null ||
    typeof value.taskActionRunId === 'string') &&
  (value.resultView === undefined ||
    value.resultView === null ||
    isRecord(value.resultView)) &&
  isContinuationPlanningStatusLike(value.continuationPlanningStatus);

const isSubjectDetailLike = (value: unknown): value is ActivitySubjectDetail =>
  isRecord(value) &&
  (value.subjectId === undefined || typeof value.subjectId === 'string') &&
  (value.accountSubjectId === null || typeof value.accountSubjectId === 'string') &&
  (value.accountAttachmentStatus === null ||
    ['account', 'attached', 'unattached', 'ambiguous'].includes(
      String(value.accountAttachmentStatus)
    )) &&
  (value.subjectKey === null || typeof value.subjectKey === 'string') &&
  typeof value.subjectKind === 'string' &&
  typeof value.subjectLabel === 'string' &&
  (value.subjectSubtitle === null || typeof value.subjectSubtitle === 'string') &&
  (value.imageUrl === undefined || value.imageUrl === null || typeof value.imageUrl === 'string') &&
  typeof value.category === 'string' &&
  typeof value.severity === 'string' &&
  (value.latestEventAt === null || typeof value.latestEventAt === 'string') &&
  (value.lastToolKey === null ||
    value.lastToolKey === undefined ||
    typeof value.lastToolKey === 'string') &&
  (value.lastToolLabel === null ||
    value.lastToolLabel === undefined ||
    typeof value.lastToolLabel === 'string') &&
  typeof value.headline === 'string' &&
  (value.nextStepLabel === null || typeof value.nextStepLabel === 'string') &&
  (value.nextStepDescription === null || typeof value.nextStepDescription === 'string') &&
  (value.nextStepAction === null ||
    value.nextStepAction === undefined ||
    isLightViewActionLike(value.nextStepAction)) &&
  isCountsLike(value.counts) &&
  Array.isArray(value.recommendations) &&
  value.recommendations.every((recommendation) => isRecommendationLike(recommendation)) &&
  Array.isArray(value.thread) &&
  value.thread.every((item) => isThreadItemLike(item)) &&
  Array.isArray(value.events) &&
  value.events.every((event) => isEventLike(event)) &&
  Array.isArray(value.proofs) &&
  value.proofs.every((proof) => isProofLike(proof)) &&
  (value.threadState === undefined ||
    value.threadState === null ||
    isThreadStateLike(value.threadState, true));

const isListResponseLike = (value: unknown): value is ActivityFeedListResponse =>
  isRecord(value) &&
  (value.threadLivingCardEnabled === undefined ||
    typeof value.threadLivingCardEnabled === 'boolean') &&
  Array.isArray(value.subjects) &&
  value.subjects.every((subject) => isSubjectCardLike(subject));

const isDetailResponseLike = (
  value: unknown
): value is ActivityFeedSubjectDetailResponse =>
  isRecord(value) &&
  (value.threadLivingCardEnabled === undefined ||
    typeof value.threadLivingCardEnabled === 'boolean') &&
  (value.threadWaitTimeEnabled === undefined ||
    typeof value.threadWaitTimeEnabled === 'boolean') &&
  (value.threadWaitGmailEnabled === undefined ||
    typeof value.threadWaitGmailEnabled === 'boolean') &&
  (value.threadWaitCalendarEnabled === undefined ||
    typeof value.threadWaitCalendarEnabled === 'boolean') &&
  (value.threadWaitHubSpotEnabled === undefined ||
    typeof value.threadWaitHubSpotEnabled === 'boolean') &&
  (value.threadChatDecisionFocusEnabled === undefined ||
    typeof value.threadChatDecisionFocusEnabled === 'boolean') &&
  (value.detail === null || isSubjectDetailLike(value.detail));

const isDismissResponseLike = (
  value: unknown
): value is ActivityFeedDismissRecommendationResponse =>
  isRecord(value) && value.ok === true;

const isSnoozeResponseLike = (value: unknown): value is ActivityFeedSnoozeTopicResponse =>
  isRecord(value) &&
  value.ok === true &&
  isRecord(value.wait) &&
  typeof value.wait.waitId === 'string' &&
  typeof value.wait.decisionId === 'string' &&
  typeof value.wait.topicId === 'string' &&
  typeof value.wait.threadId === 'string' &&
  typeof value.wait.topicVersion === 'number' &&
  typeof value.wait.threadVersion === 'number' &&
  typeof value.wait.wakeAt === 'string' &&
  typeof value.wait.timezone === 'string' &&
  value.wait.status === 'active' &&
  typeof value.wait.idempotentReplay === 'boolean';

const isCancelWaitResponseLike = (
  value: unknown
): value is ActivityFeedCancelTopicWaitResponse =>
  isRecord(value) &&
  value.ok === true &&
  isRecord(value.wait) &&
  typeof value.wait.waitId === 'string' &&
  typeof value.wait.topicId === 'string' &&
  typeof value.wait.threadId === 'string' &&
  typeof value.wait.topicVersion === 'number' &&
  typeof value.wait.threadVersion === 'number' &&
  value.wait.status === 'cancelled' &&
  typeof value.wait.idempotentReplay === 'boolean';

const isMonitorGmailResponseLike = (
  value: unknown
): value is ActivityFeedMonitorGmailReplyResponse =>
  isRecord(value) &&
  value.ok === true &&
  isRecord(value.wait) &&
  typeof value.wait.waitId === 'string' &&
  typeof value.wait.decisionId === 'string' &&
  typeof value.wait.topicId === 'string' &&
  typeof value.wait.threadId === 'string' &&
  typeof value.wait.gmailThreadRefId === 'string' &&
  typeof value.wait.topicVersion === 'number' &&
  typeof value.wait.threadVersion === 'number' &&
  typeof value.wait.expiresAt === 'string' &&
  typeof value.wait.timezone === 'string' &&
  value.wait.coverage === 'exact' &&
  value.wait.status === 'active' &&
  typeof value.wait.idempotentReplay === 'boolean';

const isMonitorExactProviderResponseLike = (
  value: unknown
): value is ActivityFeedMonitorExactProviderResponse =>
  isRecord(value) &&
  value.ok === true &&
  isRecord(value.wait) &&
  typeof value.wait.waitId === 'string' &&
  typeof value.wait.decisionId === 'string' &&
  typeof value.wait.topicId === 'string' &&
  typeof value.wait.threadId === 'string' &&
  ['google_calendar', 'hubspot'].includes(String(value.wait.provider)) &&
  typeof value.wait.providerRefId === 'string' &&
  typeof value.wait.topicVersion === 'number' &&
  typeof value.wait.threadVersion === 'number' &&
  typeof value.wait.expiresAt === 'string' &&
  typeof value.wait.timezone === 'string' &&
  value.wait.coverage === 'exact' &&
  value.wait.status === 'active' &&
  typeof value.wait.idempotentReplay === 'boolean';

const isArchiveResponseLike = (
  value: unknown
): value is ActivityFeedArchiveSubjectResponse => isRecord(value) && value.ok === true;

const isSubjectChatMessageLike = (value: unknown): value is ActivitySubjectChatMessage =>
  isRecord(value) &&
  typeof value.id === 'string' &&
  (value.role === 'user' || value.role === 'assistant') &&
  typeof value.content === 'string' &&
  (value.action === null || isLightViewActionLike(value.action));

const isSubjectChatHistoryResponseLike = (
  value: unknown
): value is ActivityFeedSubjectChatHistoryResponse =>
  isRecord(value) &&
  Array.isArray(value.messages) &&
  value.messages.every((message) => isSubjectChatMessageLike(message));

/** My file; with `memberUserId`, a member's file read by a manager (team scope). */
export const listActivitySubjects = async (memberUserId?: string): Promise<ActivityFeedListResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'list_subjects', ...(memberUserId ? { memberUserId } : {}) },
  });

  if (error) {
    throw new Error(error.message || 'Impossible de charger les sujets Activity.');
  }

  if (!isListResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (liste).');
  }

  return data;
};

export const syncKnownGmailThreads = async (): Promise<void> => {
  const { error } = await supabase.functions.invoke('gmail-sync-recent', {
    body: { maxPages: 2, maxResults: 50 },
  });

  const status =
    error && isRecord(error)
      ? isRecord(error.context) && typeof error.context.status === 'number'
        ? error.context.status
        : null
      : null;

  if (error && status !== 409) {
    throw new Error(error.message || 'Impossible de synchroniser les preuves Gmail.');
  }
};

export const getActivitySubjectDetail = async (
  request: Omit<ActivityFeedSubjectDetailRequest, 'action'>
): Promise<ActivityFeedSubjectDetailResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: {
      action: 'subject_detail',
      ...request,
    },
  });

  if (error) {
    throw new Error(error.message || 'Impossible de charger le suivi du sujet.');
  }

  if (!isDetailResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (detail).');
  }

  return data;
};

export const dismissActivityRecommendation = async (
  request: Omit<ActivityFeedDismissRecommendationRequest, 'action'>
): Promise<ActivityFeedDismissRecommendationResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: {
      action: 'dismiss_recommendation',
      ...request,
    },
  });

  if (error) {
    throw new Error(error.message || 'Impossible de dismiss cette recommandation.');
  }

  if (!isDismissResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (dismiss).');
  }

  return data;
};

export const snoozeActivityTopic = async (
  request: Omit<ActivityFeedSnoozeTopicRequest, 'action'>
): Promise<ActivityFeedSnoozeTopicResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'snooze_topic', ...request },
  });
  if (error) {
    throw new Error(error.message || 'Impossible de programmer ce rappel.');
  }
  if (!isSnoozeResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (rappel).');
  }
  return data;
};

export const confirmActivityOutcome = async (
  request: Omit<ActivityFeedConfirmOutcomeRequest, 'action'>
): Promise<ActivityFeedConfirmOutcomeResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'confirm_outcome', ...request },
  });
  if (error) {
    throw new Error((await toDashboardUiError(
      error, 'Impossible de confirmer ce résultat. Actualisez le compte et réessayez.'
    )).message);
  }
  if (!isRecord(data) || data.ok !== true || !isRecord(data.confirmation) ||
    data.confirmation.topicId !== request.topicId ||
    data.confirmation.lifecycleState !== 'completed') {
    throw new Error('La confirmation du résultat est incomplète. Actualisez le compte.');
  }
  return data as unknown as ActivityFeedConfirmOutcomeResponse;
};

export const cancelActivityTopicWait = async (
  request: Omit<ActivityFeedCancelTopicWaitRequest, 'action'>
): Promise<ActivityFeedCancelTopicWaitResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'cancel_topic_wait', ...request },
  });
  if (error) {
    throw new Error(error.message || 'Impossible d’annuler ce rappel.');
  }
  if (!isCancelWaitResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (annulation du rappel).');
  }
  return data;
};

export const monitorActivityGmailReply = async (
  request: Omit<ActivityFeedMonitorGmailReplyRequest, 'action'>
): Promise<ActivityFeedMonitorGmailReplyResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'monitor_gmail_reply', ...request },
  });
  if (error) {
    throw new Error(error.message || 'Impossible de surveiller cette réponse Gmail.');
  }
  if (!isMonitorGmailResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (surveillance Gmail).');
  }
  return data;
};

export const cancelActivityGmailWait = async (
  request: Omit<ActivityFeedCancelGmailWaitRequest, 'action'>
): Promise<ActivityFeedCancelTopicWaitResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'cancel_gmail_wait', ...request },
  });
  if (error) {
    throw new Error(error.message || 'Impossible d’annuler cette surveillance Gmail.');
  }
  if (!isCancelWaitResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (annulation Gmail).');
  }
  return data;
};

export const monitorActivityExactProvider = async (
  request: Omit<ActivityFeedMonitorExactProviderRequest, 'action'>
): Promise<ActivityFeedMonitorExactProviderResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'monitor_exact_provider', ...request },
  });
  if (error) {
    throw new Error(error.message || 'Impossible d’activer cette surveillance.');
  }
  if (!isMonitorExactProviderResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (surveillance provider).');
  }
  return data;
};

export const cancelActivityExactProviderWait = async (
  request: Omit<ActivityFeedCancelExactProviderWaitRequest, 'action'>
): Promise<ActivityFeedCancelTopicWaitResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: { action: 'cancel_exact_provider_wait', ...request },
  });
  if (error) {
    throw new Error(error.message || 'Impossible d’arrêter cette surveillance.');
  }
  if (!isCancelWaitResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (annulation provider).');
  }
  return data;
};

export const getSubjectChatHistory = async (
  request: Omit<ActivityFeedSubjectChatHistoryRequest, 'action'>
): Promise<ActivityFeedSubjectChatHistoryResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: {
      action: 'subject_chat_history',
      ...request,
    },
  });

  if (error) {
    throw new Error(error.message || 'Impossible de charger l’historique du sujet.');
  }

  if (!isSubjectChatHistoryResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (historique chat).');
  }

  return data;
};

export const archiveActivitySubject = async (
  request: Omit<ActivityFeedArchiveSubjectRequest, 'action'>
): Promise<ActivityFeedArchiveSubjectResponse> => {
  const { data, error } = await supabase.functions.invoke('activity-feed', {
    body: {
      action: 'archive_subject',
      ...request,
    },
  });

  if (error) {
    throw new Error(error.message || 'Impossible d’archiver ce sujet.');
  }

  if (!isArchiveResponseLike(data)) {
    throw new Error('Réponse invalide de activity-feed (archive).');
  }

  return data;
};
